/**
 * Localised labels for BE-defined system fields and their enum options.
 *
 * The BE ships a fixed catalogue of system fields (`SystemFieldKey.*`)
 * with English `name`s baked in (`"First name"`, `"Chat status"`, …).
 * The widget renders the visitor-facing form in the visitor's UI
 * language, so we shadow those English names with translations from
 * `messages/<locale>.json` whenever the field's key is one of the
 * known system keys. Operator-defined custom fields (anything not in
 * `KNOWN_SYSTEM_FIELDS`) keep the operator-supplied `label` since their
 * names are configurable per integration and not in our string table.
 *
 * Same story for ENUM/ARRAY options: known option lists (chat status,
 * priority, lead status, …) get localised display labels; everything
 * else falls back to `humanise()` of the wire value.
 */

import { humanise } from "./field-controls/options-control";

/** Convert a BE field key — which can be snake_case (`chat_status`) or
 *  camelCase (`firstName`) depending on field vintage — to a canonical
 *  camelCase form for table lookup. */
function camelize(key: string): string {
  return key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * Every system-field key the BE defines (mirrors `SystemFieldKey.*`
 * in `backend-app`). Used as the allow-list for translation lookup —
 * keys NOT in this set are operator-defined custom fields whose
 * labels we don't own.
 *
 * Keep alphabetical within each group for diff-friendliness.
 */
const KNOWN_SYSTEM_FIELDS = new Set([
  // Identity
  "avatar",
  "wexioId",
  "telegramId",
  "whatsappId",
  "viberId",
  "instagramId",
  "optInWhatsapp",
  "webVerified",
  "firstName",
  "lastName",
  "username",
  "language",
  "email",
  "phone",
  "company",
  "location",
  "isValid",
  // Chat status + context
  "chatStatus",
  "chatCategory",
  "priority",
  // Lead + sales
  "leadSource",
  "leadStatus",
  "interestLevel",
  "productInterest",
  "budgetRange",
  "purchaseTimeline",
  // Communication timestamps
  "lastMessageSentAt",
  "lastMessageReceivedAt",
  "lastSeenAt",
  "firstMessageReceivedAt",
  // Status
  "isBlocked",
  "registrationDate",
  "aiAvailable",
  // Metrics
  "totalMessagesSent",
  "totalMessagesReceived",
  "totalAiMessagesSent",
  "totalBotMessagesSent",
  "totalUserMessagesSent",
  "flowCompletionRate",
  "lastFlowCompleted",
  "totalFlowsStarted",
  "totalFlowsCompleted",
]);

/** Fields whose `options[]` enums we localise. Anything not listed
 *  falls back to `humanise()` of the BE wire value, so a new enum
 *  field added to the BE still renders sensibly without a widget
 *  release. */
const KNOWN_SYSTEM_OPTIONS = new Set([
  "chatStatus",
  "chatCategory",
  "priority",
  "leadStatus",
  "interestLevel",
]);

/**
 * Translate a field label. Returns the i18n string when `fieldKey`
 * matches a known system field; otherwise returns the BE-supplied
 * `fallback` unchanged (custom fields).
 */
export function localizeFieldLabel(
  fieldKey: string,
  fallback: string,
  t: (key: string) => string,
): string {
  const k = camelize(fieldKey);
  if (!KNOWN_SYSTEM_FIELDS.has(k)) return fallback;
  return t(`systemFields.${k}`);
}

/**
 * Translate one option value within a known enum field. Falls back to
 * `humanise(value)` (`IN_PROGRESS` → `In progress`) when the field's
 * option list isn't one we curate translations for.
 */
export function localizeOptionLabel(
  fieldKey: string,
  optionValue: string,
  t: (key: string) => string,
): string {
  const k = camelize(fieldKey);
  if (!KNOWN_SYSTEM_OPTIONS.has(k)) return humanise(optionValue);
  return t(`systemFieldOptions.${k}.${optionValue}`);
}
